import { useCallback } from 'react';
import { useHistory } from 'react-router';
import { useQuery } from '@apollo/client';

import { OutstandingAgreementType, PackageType } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import Agreement from 'common/bento/modules/Agreement';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SuspenseQuery from 'components/SuspenseQuery';
import NavigationContext from 'features/Onboarding/components/OBNavigation/NavigationContext';
import OnboardingPageAdapter from 'features/Onboarding/components/OnboardingPageAdapter';
import BeneficiariesDocs from 'features/Onboarding/legacy/Pages/BeneficiariesDocs';
import DownloadShineMessageSent from 'features/Onboarding/legacy/Pages/DownloadShineMessageSent';
import CompanyCompliance from 'features/Onboarding/modules/CompanyCompliance';
import PersonalInfo from 'features/Onboarding/modules/PersonalInfo/PersonalInfo';
import PricingPlan from 'features/Onboarding/modules/PricingPlan';
import Verification from 'features/Onboarding/modules/Verification';
import CapitalDepositNextSteps from 'features/Onboarding/pages/CapitalDepositNextSteps';
import logger from 'helpers/logger';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import useOverrideSetCurrentPage from '../../hooks/useOverrideSetCurrentPage';
import { User } from '../../legacy/types';
import { ApplicationTypeContextType } from '../../lib/applicationType';
import Beneficiaries from '../../modules/Beneficiaries';
import CapitalDepositEUBankAccount from '../../modules/CapitalDepositEUBankAccount';
import CapitalDepositQuestions from '../../modules/CapitalDepositQuestions';
import { ApplicantType, ApplicationType } from '../../types';
import { CapitalDepositDataDocument } from './graphql/queries/capitalDepositData.gql';
import machineConfig, { model, State } from './machine';
import getNavigationSteps from './navigation/getNavigationSteps';
import ApplicationFollowUp from './pages/ApplicationFollowUp';
import CapitalDepositGuide from './pages/CapitalDepositGuide';

const logImpossibleEvent = (event: string, state: string) => {
  logger.error(`Impossible event '${event}' in state ${state}`);
};

const OnboardingCapitalDepositComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const adaptedPageProps = {
      onPrev,
      setCurrentPage: useOverrideSetCurrentPage(send),
      user: moduleData.viewer.profile as unknown as User,
    };

    const stringifiedState = stringifyState<State>(state);

    const getContent = () => {
      switch (stringifiedState) {
        case State.CAPITAL_DEPOSIT_GUIDE:
          return (
            <CapitalDepositGuide
              onContinue={() => send('NEXT')}
              onPrev={onPrev}
            />
          );

        case State.CAPITAL_DEPOSIT_AGREEMENT: {
          return (
            <Agreement
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
              type={OutstandingAgreementType.NotaryFourez}
            />
          );
        }

        case State.USER_AGREEMENT: {
          return (
            <Agreement
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
              type={OutstandingAgreementType.Cgu}
            />
          );
        }

        case State.CAPITAL_DEPOSIT_EU_BANK_ACCOUNT:
          return (
            <CapitalDepositEUBankAccount
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
            />
          );

        case State.INIT:
        case State.USER_PROFILE_SEND:
        case State.DONE:
        case State.ABORT:
        case State.ABORT_APPLICATION:
        case State.CAPITAL_DEPOSIT_SUBMIT_APPLICATION:
          return <OBLoaderPage onPrev={() => null} />;
      }

      const { company } = moduleData.viewer;
      if (!company) {
        throw new Error(`Company not found for state ${stringifiedState}`);
      }

      switch (stringifiedState) {
        case State.PERSONAL_INFO:
          return (
            <PersonalInfo
              applicantType={ApplicantType.AccountHolder}
              applicationType={ApplicationType.CapitalDeposit}
              companyProfileId={company.companyProfileId}
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.ABORT
                  ? send('PREV')
                  : send('NEXT')
              }
            />
          );

        case State.VERIFICATION: {
          return (
            <Verification
              applicantType={ApplicantType.AccountHolder}
              applicationType={ApplicationType.CapitalDeposit}
              companyProfileId={company.companyProfileId}
              isMainApplicant
              onDone={() => send('NEXT')}
            />
          );
        }

        case State.COMPANY_COMPLIANCE: {
          if (!company) {
            throw new Error(`companyProfileId required`);
          }

          return (
            <CompanyCompliance
              companyProfileId={company.companyProfileId}
              isMandatee={false}
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
            />
          );
        }

        case State.CAPITAL_DEPOSIT_QUESTIONS: {
          return (
            <CapitalDepositQuestions
              companyProfileId={company.companyProfileId}
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
            />
          );
        }

        case State.BENEFICIARIES: {
          return (
            <Beneficiaries
              applicantType={ApplicantType.AccountHolder}
              applicationType={ApplicationType.CapitalDeposit}
              companyProfileId={company.companyProfileId}
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
            />
          );
        }

        case State.CAPITAL_DEPOSIT_PLAN_CHOICE: {
          return (
            <PricingPlan
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send('NEXT')
                  : send('PREV')
              }
              packageType={PackageType.CapitalDeposit}
            />
          );
        }

        case State.APPLICATION_FOLLOW_UP: {
          if (!company?.capitalDeposit) {
            throw new Error(
              `capitalDeposit required in state ${stringifiedState}`,
            );
          }
          if (company.profile.legalNumberOfShares === null) {
            throw new Error(
              `legalNumberOfShares required in state ${stringifiedState}`,
            );
          }
          if (company.capitalDeposit.paidUpCapitalPercentage === null) {
            throw new Error(
              `paidUpCapitalPercentage required in state ${stringifiedState}`,
            );
          }

          return (
            <ApplicationFollowUp
              applicationFollowUpData={{
                capitalDeposit: {
                  ...company.capitalDeposit,
                  paidUpCapitalPercentage:
                    company.capitalDeposit.paidUpCapitalPercentage,
                },
                companyProfile: {
                  ...company.profile,
                  legalNumberOfShares: company.profile.legalNumberOfShares,
                },
                kybDocuments: company.kybDocuments,
                legalBeneficiaries: company.legalBeneficiaries,
                userProfile: {
                  ...moduleData.viewer.profile,
                  email: moduleData.viewer.companyUser?.email ?? '',
                },
              }}
              isInitialSubmission={
                state.event.type === 'done.invoke.submitApplication'
              }
              onPrev={onPrev}
            />
          );
        }

        case State.CAPITAL_DEPOSIT_NEXT_STEPS: {
          return (
            <CapitalDepositNextSteps
              goToBeneficiaries={() => send('GO_BENEFICIARIES_DOCS')}
              goToSmsSent={() => send('DOWNLOAD_SHINE')}
              onPrev={onPrev}
            />
          );
        }

        case State.BENEFICIARIES_DOCS: {
          return (
            <OnboardingPageAdapter
              {...adaptedPageProps}
              LegacyOnboardingPage={BeneficiariesDocs}
              setCurrentPage={(page) => {
                switch (page) {
                  case 'verification-home':
                    return send('PREV');

                  default:
                    return logImpossibleEvent(page, stringifiedState);
                }
              }}
            />
          );
        }

        case State.DOWNLOAD_SHINE_MESSAGE_SENT: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={DownloadShineMessageSent}
              {...adaptedPageProps}
              onPrev={onPrev}
            />
          );
        }

        default: {
          return assertNever(stringifiedState);
        }
      }
    };

    return (
      <NavigationContext.Provider
        value={{ steps: getNavigationSteps(stringifyState<State>(state)) }}
      >
        {getContent()}
      </NavigationContext.Provider>
    );
  },

  {
    devTools: true,
    machineConfig,
  },
);

interface OnboardingCapitalDepositProps {
  companyProfileId?: string;
  applicationTypeContext: ApplicationTypeContextType;
}

const OnboardingCapitalDeposit = asBentoModule<OnboardingCapitalDepositProps>(
  ({ applicationTypeContext, companyProfileId, onDone }) => {
    const history = useHistory();
    const companyContext = useCompanyContext();

    const query = useQuery(CapitalDepositDataDocument, {
      variables: {
        companyProfileId: companyProfileId ?? '',
        includeCompany: Boolean(companyProfileId),
      },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(capitalDepositData) => (
          <OnboardingCapitalDepositComponent
            moduleData={{
              ...capitalDepositData,
              applicationTypeContext,
              companyContext,
              getQueryParams: () =>
                new URLSearchParams(history.location.search),
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

export default OnboardingCapitalDeposit;
